"use client";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { CSSProperties, ComponentProps } from "react";
import { cn } from "../../lib/utils";

interface ScaleContextValue {
  handleMouseDown: (e: React.MouseEvent) => void;
  registerTouchElement: (element: HTMLElement | null) => void;
  orientation: "horizontal" | "vertical";
}

const ScaleContext = createContext<ScaleContextValue | null>(null);

export interface ScaleRootProps
  extends Omit<ComponentProps<"div">, "value" | "onChange" | "onValueChange"> {
  value: number[];
  onValueChange: (value: number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  orientation?: "horizontal" | "vertical";
}

function ScaleRoot({
  value,
  onValueChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  orientation = "horizontal",
  className,
  children,
  ...props
}: ScaleRootProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const touchElementsRef = useRef<Set<HTMLElement>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [dragValue, setDragValue] = useState<number | null>(null);

  const getValueFromPosition = useCallback(
    (clientX: number, clientY: number) => {
      if (!rootRef.current) return null;

      const rect = rootRef.current.getBoundingClientRect();
      let percentage: number;

      if (orientation === "vertical") {
        const y = clientY - rect.top;
        percentage = y / rect.height;
      } else {
        const x = clientX - rect.left;
        percentage = x / rect.width;
      }

      percentage = Math.max(0, Math.min(1, percentage));
      const rawValue = min + percentage * (max - min);
      const steppedValue = Math.round(rawValue / step) * step;
      return Math.max(min, Math.min(max, steppedValue));
    },
    [min, max, step, orientation],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      setIsDragging(true);

      const newValue = getValueFromPosition(e.clientX, e.clientY);
      if (newValue !== null) {
        setDragValue(newValue);
        onValueChange([newValue]);
      }
    },
    [disabled, getValueFromPosition, onValueChange],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || disabled) return;

      const newValue = getValueFromPosition(e.clientX, e.clientY);
      if (newValue !== null && dragValue !== newValue) {
        setDragValue(newValue);
        onValueChange([newValue]);
      }
    },
    [isDragging, disabled, getValueFromPosition, dragValue, onValueChange],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragValue(null);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (disabled) return;
      e.preventDefault();
      setIsDragging(true);

      const touch = e.touches[0];
      if (!touch) return;
      const newValue = getValueFromPosition(touch.clientX, touch.clientY);
      if (newValue !== null) {
        setDragValue(newValue);
        onValueChange([newValue]);
      }
    },
    [disabled, getValueFromPosition, onValueChange],
  );

  const registerTouchElement = useCallback((element: HTMLElement | null) => {
    if (element) {
      touchElementsRef.current.add(element);
    }
  }, []);

  useEffect(() => {
    const elements = touchElementsRef.current;
    elements.forEach(element => {
      element.addEventListener("touchstart", handleTouchStart, { passive: false });
    });

    return () => {
      elements.forEach(element => {
        element.removeEventListener("touchstart", handleTouchStart);
      });
    };
  }, [handleTouchStart]);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging || disabled) return;
      e.preventDefault();

      const touch = e.touches[0];
      if (!touch) return;
      const { clientX, clientY } = touch;
      const newValue = getValueFromPosition(clientX, clientY);
      if (newValue !== null && dragValue !== newValue) {
        setDragValue(newValue);
        onValueChange([newValue]);
      }
    },
    [isDragging, disabled, getValueFromPosition, dragValue, onValueChange],
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setDragValue(null);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("touchmove", handleTouchMove, { passive: false });
      document.addEventListener("touchend", handleTouchEnd);
      return () => {
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging, handleTouchMove, handleTouchEnd]);

  return (
    <ScaleContext.Provider value={{ handleMouseDown, registerTouchElement, orientation }}>
      <div
        ref={rootRef}
        className={cn(
          "relative flex touch-none select-none",
          orientation === "vertical" ? "flex-col" : "flex-row",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
        data-orientation={orientation}
        {...props}
      >
        {children}
      </div>
    </ScaleContext.Provider>
  );
}

export interface ScaleTrackProps extends ComponentProps<"div"> {}

function ScaleTrack({ className, children, ...props }: ScaleTrackProps) {
  const context = useContext(ScaleContext);

  return (
    <div
      ref={context?.registerTouchElement}
      className={cn(
        "relative pointer-events-auto before:absolute before:content-[''] before:inset-0 before:block",
        className,
      )}
      onMouseDown={context?.handleMouseDown}
      {...props}
    >
      {children}
    </div>
  );
}

export interface ScaleThumbProps extends ComponentProps<"div"> {
  style?: CSSProperties;
}

function ScaleThumb({ className, style, ...props }: ScaleThumbProps) {
  const context = useContext(ScaleContext);

  return (
    <div
      ref={context?.registerTouchElement}
      className={cn("absolute cursor-pointer", className)}
      style={style}
      onMouseDown={context?.handleMouseDown}
      {...props}
    />
  );
}

export const Scale = {
  Root: ScaleRoot,
  Track: ScaleTrack,
  Thumb: ScaleThumb,
};
